import { federalStateNames, type Config, type ConfigField, type FederalStateName } from '@/ts/types/config.types'
import type { PartnerConnectConfiguration } from '@/ts/types/partner-connect.types'

const fieldMap: Record<string, string> = {
  purchase_price: 'purchase_price',
  min_purchase_price: 'purchase_price',
  max_purchase_price: 'purchase_price',
  uvp: 'uvp_price',
  min_uvp: 'uvp_price',
  max_uvp: 'uvp_price',
  brutto: 'monthly_salary',
  min_brutto: 'monthly_salary',
  max_brutto: 'monthly_salary',
  tax_class: 'tax_class',
  employer_contribution_to_the_lease_rate: 'employer_subsidy',
  employer_contribution_to_the_lease_rate_changeable: 'employer_subsidy',
  employer_contribution_to_the_lease_rate_display: 'employer_subsidy',
  selected_employer_is_entitled_to_deduct_input_tax: 'employer_tax_deduction',
  selected_employer_is_entitled_to_deduct_input_tax_changeable: 'employer_tax_deduction',
  selected_employer_is_entitled_to_deduct_input_tax_display: 'employer_tax_deduction',
  insurance_coverage: 'assumption_of_insurance_costs',
  insurance_coverage_changeable: 'assumption_of_insurance_costs',
  state: 'federal_state',
  state_changeable: 'federal_state',
  child_tax: 'child_allowance',
  health_insurance: 'health_insurance',
  additional_health_insurance_contribution: 'health_insurance_additional_contribution',
  church_tax: 'church_tax',
  compulsory_pension: 'pension_insurance'
}

/** Converts Partner-Connect number values, including German decimal notation. */
const parsePartnerConnectNumber = (value: unknown, name: string): number => {
  const result = Number(String(value).replace(',', '.'))
  if (!Number.isFinite(result)) throw new Error(`[Partner Connect]: invalid number for ${name}`)
  return result
}

/** Converts Partner-Connect boolean values from true booleans or boolean strings. */
const parsePartnerConnectBoolean = (value: unknown, name: string): boolean => {
  if (value === true || value === false) return value
  if (value === 'true') return true
  if (value === 'false') return false
  throw new Error(`[Partner Connect]: invalid boolean for ${name}`)
}

/**
 * Normalizes a Partner-Connect API field value to the calculator's expected type / value.
 *
 * @param name - Name of the field to normalize
 * @param value - Value of the field to normalize
 * @returns Normalized value
 */
const normalizePartnerConnectFieldValue = (name: string, value: unknown): unknown => {
  switch (name) {
    case 'purchase_price':
    case 'uvp':
    case 'brutto':
    case 'employer_contribution_to_the_lease_rate':
    case 'additional_health_insurance_contribution':
      return parsePartnerConnectNumber(value, name)

    case 'tax_class': {
      const result = parsePartnerConnectNumber(value, name)
      if (!Number.isInteger(result) || result < 1 || result > 6) throw new Error('[Partner Connect]: invalid tax_class')
      return result
    }

    case 'child_tax': {
      if (value === 'none') return 'Keine Kinder'
      const result = parsePartnerConnectNumber(value, name)
      if (result < 0 || result > 6 || result % 0.5 !== 0) throw new Error(`[Partner Connect]: invalid child_tax`)
      return result
    }

    case 'health_insurance':
      if (value === 'privat') return false
      if (value === 'gesetzlich') return true
      throw new Error('[Partner Connect]: invalid health_insurance')

    case 'insurance_coverage':
      if (value === 'employer') return true
      if (value === 'employee') return false
      throw new Error('[Partner Connect]: invalid insurance_coverage')

    case 'church_tax':
    case 'compulsory_pension':
    case 'selected_employer_is_entitled_to_deduct_input_tax':
      return parsePartnerConnectBoolean(value, name)

    case 'state':
      if (typeof value !== 'string' || !federalStateNames.includes(value as FederalStateName))
        throw new Error('[Partner Connect]: invalid state')
      return value as FederalStateName

    default:
      return value
  }
}

/**
 * Updates a named calculator field with the given partial update.
 *
 * @param fields - Array of config fields
 * @param name - Name of the field to update
 * @param update - Partial update to apply
 */
const updateConfigField = (fields: ConfigField[], name: string, update: Partial<ConfigField>) => {
  const field = fields.find((item) => item.name === name)
  if (!field) throw new Error(`[Partner Connect]: unknown config field ${name}`)
  Object.assign(field, update)
}

/**
 * Restores invalid min and max bounds in the normalized config based on the base config.
 * @param config - The normalized calculator config to update
 * @param baseConfig - The original base calculator config to reference for restoring invalid bounds
 */
const restoreInvalidBounds = (config: Config, baseConfig: Config) => {
  config.fields.forEach((field) => {
    if (typeof field.default !== 'number') return

    const baseField = baseConfig.fields.find((item) => item.name === field.name)
    if (!baseField) return

    if (field.min !== null && field.default < field.min) field.min = baseField.min
    if (field.max !== null && field.default > field.max) field.max = baseField.max
  })
}

/**
 * Creates a normalized calculator config without mutating the base config.
 * @param baseConfig - The base calculator config
 * @param configuration - The Partner Connect configuration to normalize
 * @returns The normalized calculator config
 */
export const normalizePartnerConnectConfig = (baseConfig: Config, configuration: PartnerConnectConfiguration): Config => {
  const config = structuredClone(baseConfig)
  const fields = configuration.fields
  const values = fields && typeof fields === 'object' && !Array.isArray(fields) ? (fields as Record<string, unknown>) : configuration

  for (const [sourceName, rawValue] of Object.entries(values)) {
    if (rawValue === null || rawValue === undefined) continue

    const targetName = fieldMap[sourceName]
    if (!targetName) continue

    if (sourceName.endsWith('_changeable')) {
      updateConfigField(config.fields, targetName, { read_only: !parsePartnerConnectBoolean(rawValue, sourceName) })
      continue
    }

    if (sourceName.endsWith('_display')) {
      updateConfigField(config.fields, targetName, { hide: !parsePartnerConnectBoolean(rawValue, sourceName) })
      continue
    }

    const update: Partial<ConfigField> = {}
    if (sourceName.startsWith('min_')) update.min = parsePartnerConnectNumber(rawValue, sourceName)
    else if (sourceName.startsWith('max_')) update.max = parsePartnerConnectNumber(rawValue, sourceName)
    else update.default = normalizePartnerConnectFieldValue(sourceName, rawValue) as ConfigField['default']

    updateConfigField(config.fields, targetName, update)
  }

  if (values.lease_factor !== null && values.lease_factor !== undefined)
    config.factors.leasing_factor = parsePartnerConnectNumber(values.lease_factor, 'lease_factor')

  if (values.percentage !== null && values.percentage !== undefined)
    config.factors.expected_used_purchase_price_factor = parsePartnerConnectNumber(values.percentage, 'percentage') / 100

  restoreInvalidBounds(config, baseConfig)

  return config
}
